import type { AuthUser } from '@/features/auth/types';
import type { Notification } from './notificationsStore';

/**
 * Entity type → route mapping for direct navigation.
 * Note: Only routes with /:id support are included here.
 * Routes without detail pages (documents, tasks, fees) go to list view.
 */
const ENTITY_ROUTES_WITH_ID: Record<string, string> = {
  Case: '/matters',
  Matter: '/matters',
  Hearing: '/hearings',
  // Contact handled separately - barristers have /clients, others have /contacts/:id
  // Judgment handled separately - clerks don't have judgment routes
};

/**
 * Entity types that only have list pages (no detail route).
 */
const ENTITY_ROUTES_LIST_ONLY: Record<string, string> = {
  Document: '/documents',
  FeeRecord: '/fees',
  FeeNote: '/fees',
  Task: '/tasks',
  CalendarEvent: '/diary',
  // Note: There's no /briefs route - allocations show on dashboard/bookings
};

/**
 * Resolve a notification → URL path appropriate for the *current user's*
 * role. Uses entityType and entityId when available for direct navigation.
 */
export function resolveNotificationLink(
  n: Notification,
  user: AuthUser | null,
): string | undefined {
  if (!user) return n.link;

  const basePath = basePathFor(user);

  // If we have entityType and entityId, navigate directly to that entity
  if (n.entityType && n.entityId) {
    // Support tickets are cross-role and owned by the requester, so the destination is the
    // member-scoped path rather than a basePath-derived one. Handled before the generic maps
    // because there is no role variant to resolve.
    if (n.entityType === 'SupportTicket') {
      return `/me/support/${n.entityId}`;
    }
    // Special case: messages/threads need query param
    if (n.entityType === 'Message' || n.entityType === 'Thread' || n.entityType === 'MessageThread') {
      const commsPath = basePath === '/solicitor' ? '/messages' : '/communications';
      return `${basePath}${commsPath}?thread=${n.entityId}`;
    }

    // Special case: contacts - barristers use /clients/:id, others use /contacts/:id
    if (n.entityType === 'Contact') {
      return basePath === '/barrister'
        ? `${basePath}/clients/${n.entityId}`
        : `${basePath}/contacts/${n.entityId}`;
    }

    // Judgments - all roles now have access
    if (n.entityType === 'Judgment') {
      return `${basePath}/judgments/${n.entityId}`;
    }

    // Routes with detail pages (/:id)
    const routeWithId = ENTITY_ROUTES_WITH_ID[n.entityType];
    if (routeWithId) {
      return `${basePath}${routeWithId}/${n.entityId}`;
    }

    // Routes that only have list pages
    const routeListOnly = ENTITY_ROUTES_LIST_ONLY[n.entityType];
    if (routeListOnly) {
      if (n.entityType === 'CalendarEvent' && basePath === '/admin') {
        return '/admin/calendar';
      }
      return `${basePath}${routeListOnly}`;
    }

    // Allocations: clerks go to bookings, barristers go to dashboard
    if (n.entityType === 'Allocation') {
      return basePath === '/clerk' ? `${basePath}/bookings` : basePath;
    }
  }

  // Messaging: jump straight into the relevant thread
  if (n.type === 'message_received') {
    const threadId = n.entityId ?? extractThreadIdFromLink(n.link);
    const commsPath = basePath === '/solicitor' ? '/messages' : '/communications';
    if (threadId) return `${basePath}${commsPath}?thread=${threadId}`;
    return `${basePath}${commsPath}`;
  }

  // New instruction from the public intake form - go to the intake inbox.
  // Recipients span admin/clerk/silk/junior, each with their own basePath.
  if (n.type === 'intake_received' || n.entityType === 'IntakeRequest') {
    return `${basePath}/intake`;
  }

  // Matter needs a barrister - send clerks/admins to the allocation workspace.
  if (n.type === 'matter_needs_allocation') {
    return basePath === '/clerk' ? `${basePath}/bookings` : `${basePath}/matters`;
  }

  // Matter created / assigned - go to the matter
  if ((n.type === 'matter_created' || n.type === 'case_assigned') && n.entityId) {
    return `${basePath}/matters/${n.entityId}`;
  }

  // Hearing booked/assigned/reminder/outcome pending - go to the hearing
  if ((n.type === 'hearing_booked' || n.type === 'hearing_assigned' ||
       n.type === 'hearing_reminder' || n.type === 'hearing_outcome_pending') && n.entityId) {
    return `${basePath}/hearings/${n.entityId}`;
  }

  // Judgment reserved - go to the hearing where judgment was reserved
  if (n.type === 'judgment_reserved' && n.entityId) {
    return `${basePath}/hearings/${n.entityId}`;
  }

  // Matter inactive - go to the matter
  if (n.type === 'matter_inactive' && n.entityId) {
    return `${basePath}/matters/${n.entityId}`;
  }

  // Task assigned / due - go to the matter's tasks tab
  if ((n.type === 'task_assigned' || n.type === 'task_due') && n.entityId) {
    return n.link ?? `${basePath}/matters`;
  }

  // Conference notifications
  if (n.type === 'conference_requested' || n.type === 'conference_scheduled' ||
      n.type === 'conference_cancelled' || n.type === 'conference_completed') {
    if (n.entityId) return `${basePath}/matters/${n.entityId}`;
    return basePath === '/clerk' ? `${basePath}/bookings` : basePath;
  }

  // Document uploaded - go to the matter's documents
  if (n.type === 'document_uploaded' && n.entityId) {
    return `${basePath}/matters/${n.entityId}?tab=documents`;
  }

  // Fee notes - all roles have fees page
  if (n.type === 'fee_note_issued' || n.type === 'fee_note_paid' || n.type === 'fee_note_pending') {
    return `${basePath}/fees`;
  }

  // Compliance notifications - no specific page yet, go to matters
  if (n.type === 'kyc_expiring' || n.type === 'limitation_approaching' ||
      n.type === 'conflict_check_required') {
    return `${basePath}/matters`;
  }

  // Brief allocation notifications - clerks go to bookings, barristers to dashboard
  if (n.type === 'brief_received' || n.type === 'brief_returned' ||
      n.type === 'brief_accepted' || n.type === 'brief_declined' ||
      n.type === 'brief_withdrawn') {
    return basePath === '/clerk' ? `${basePath}/bookings` : basePath;
  }

  // Clerk-barrister assignment changes. Same type serves two recipients:
  //  • the CLERK who was added/removed → their assigned-barristers roster
  //  • the BARRISTER (nudge to configure permissions) → their delegation page (n.link)
  // Explicit + role-aware so this never silently depends on the entityType fallthrough.
  if (n.type === 'clerk_assigned' || n.type === 'clerk_unassigned') {
    if (basePath === '/clerk') return `${basePath}/my-barristers`;
    // Only barristers have /my-clerks; admins have no such page → fall back to their dashboard.
    return n.link ?? (basePath === '/barrister' ? `${basePath}/my-clerks` : basePath);
  }

  // Policy published/updated → the member's shared "My Policies" acceptance page.
  // Explicit (not basePath-prefixed): /me/policies is the shared member route.
  if (n.type === 'policy_published') return '/me/policies';

  // Fallback: rewrite /admin prefix to user's basePath
  if (n.link?.startsWith('/admin/')) {
    let sliced = n.link.slice('/admin'.length);
    if (sliced.startsWith('/cases')) {
      sliced = '/matters' + sliced.slice('/cases'.length);
    }
    return basePath + sliced;
  }
  return n.link;
}

function basePathFor(user: AuthUser): string {
  if (user.memberType === 'admin' || user.memberType === 'staff') return '/admin';
  if (user.memberType === 'senior_clerk' || user.memberType === 'junior_clerk') return '/clerk';
  if (user.memberType === 'solicitor') return '/solicitor';
  return '/barrister';
}

function extractThreadIdFromLink(link?: string): string | null {
  if (!link) return null;
  const match = link.match(/[?&]thread=([0-9a-f-]{8,})/i);
  return match ? match[1] ?? null : null;
}
